import React, { useEffect, useState } from 'react';
import { useLocalize } from '@saviynt/common';
import {
  Box,
  Button,
  CheckboxGroup,
  FooterActionBar,
  Icon,
  ModalIsland,
  Typography,
  useCheckboxGroup,
} from '@saviynt/design-system';
import PropTypes from 'prop-types';

import { msgs } from '../../../../utilities/messages';
import { ACCOUNT_JIT_SELECTION } from '../../constants';

import './DatabaseJitModalIsland.css';

function DatabaseJitModalIsland({
  isOpen,
  setIsDatabaseJitCreateAccount,
  setDatabaseJitAccountInfo,
  databaseJitAccountInfo,
  setAccountSelection,
  databaseJitModalContentRef,
}) {
  const localize = useLocalize();
  const SELECT_ALL = localize(msgs.pam.common.selectAll);
  const DESELECT_ALL = localize(msgs.pam.common.deselectAll);
  const [checkboxButtonLabel, setCheckboxButtonLabel] = useState(SELECT_ALL);

  const checkboxConfigs = [
    {
      name: 'SELECT',
      description: localize(msgs.pam.common.db.selectLabel),
      isChecked: false,
      isNoWrap: false,
      labelLineLimit: 4,
      kind: 'multiLine',
      label: localize(msgs.pam.common.db.select).toUpperCase(),
      showHoverLabel: true,
      className: 'DatabaseJitModal-checkbox',
    },
    {
      name: 'ALTER',
      isChecked: false,
      isNoWrap: false,
      description: localize(msgs.pam.common.db.alterLabel),
      labelLineLimit: 4,
      kind: 'multiLine',
      label: localize(msgs.pam.common.db.alter).toUpperCase(),
      showHoverLabel: true,
      className: 'DatabaseJitModal-checkbox',
    },
    {
      name: 'CREATE',
      isChecked: false,
      isNoWrap: false,
      description: localize(msgs.pam.common.db.createLabel),
      labelLineLimit: 4,
      kind: 'multiLine',
      label: localize(msgs.pam.common.db.create).toUpperCase(),
      showHoverLabel: true,
      className: 'DatabaseJitModal-checkbox',
    },
    {
      name: 'INSERT',
      isChecked: false,
      description: localize(msgs.pam.common.db.insertLabel),
      labelLineLimit: 4,
      kind: 'multiLine',
      label: localize(msgs.pam.common.db.insert).toUpperCase(),
      showHoverLabel: true,
      className: 'DatabaseJitModal-checkbox',
    },
    {
      name: 'UPDATE',
      isChecked: false,
      isNoWrap: false,
      description: localize(msgs.pam.common.db.updateLabel),
      labelLineLimit: 4,
      kind: 'multiLine',
      label: localize(msgs.pam.common.db.update).toUpperCase(),
      showHoverLabel: true,
      className: 'DatabaseJitModal-checkbox',
    },
  ];

  const {
    checkboxes,
    checkboxStates,
    checkAllCheckboxes,
    uncheckAllCheckboxes,
    getCheckedNames,
    setCheckedFromArrayOfNames,
  } = useCheckboxGroup(checkboxConfigs);

  const handleCheckboxButtonClick = () => {
    if (checkboxButtonLabel === SELECT_ALL) {
      checkAllCheckboxes();
    } else {
      uncheckAllCheckboxes();
    }
  };

  const handleSave = () => {
    // send info back to the parent.
    setDatabaseJitAccountInfo((prevState) => ({
      ...prevState,
      entitlements: getCheckedNames(),
    }));
    setAccountSelection(ACCOUNT_JIT_SELECTION);
    setIsDatabaseJitCreateAccount(false);
  };

  useEffect(() => {
    const areAllChecked = checkboxStates.every(
      (checkbox) => checkbox.isChecked
    );

    setCheckboxButtonLabel(areAllChecked ? DESELECT_ALL : SELECT_ALL);
  }, [checkboxStates]);

  useEffect(() => {
    setCheckedFromArrayOfNames(databaseJitAccountInfo.entitlements);
  }, []);

  const renderedModalContent = (
    <Box tag='section'>
      <Typography kind='h2'>
        {localize(msgs.pam.cts.db.jitAccountDetails)}
      </Typography>
      <Box className='DatabaseJitModal-accountDetailsWrapper'>
        <Box className='DatabaseJitModal-accountNameWrapper'>
          <Typography kind='h4' className='DatabaseJitModal-accountName'>
            {localize(msgs.pam.cts.db.jitAccountName)}
          </Typography>
          <Typography
            kind='body2'
            className='DatabaseJitModal-accountNameValue'>
            {localize(msgs.pam.cts.db.jitAccount)}
          </Typography>
        </Box>
        <Box className='DatabaseJitModal-accountDescWrapper'>
          <Typography kind='h4' className='DatabaseJitModal-accountDesc'>
            {localize(msgs.pam.cts.db.jitAccountDesc)}
          </Typography>
          <Typography
            kind='body2'
            className='DatabaseJitModal-accountDescValue'>
            {localize(msgs.pam.cts.db.jitAccountValue)}
          </Typography>
        </Box>
      </Box>
      <div className='DatabaseJitModal-entitlementsHeaderWrapper'>
        <Typography kind='h2'>
          {localize(msgs.pam.cts.db.jitEntitlementsNeeded)}
        </Typography>
        <Button onClick={handleCheckboxButtonClick}>
          {checkboxButtonLabel}
        </Button>
      </div>
      <Box className='DatabaseJitModal-checkboxes'>
        <CheckboxGroup
          checkboxes={checkboxes}
          classNames={{
            fieldsetClassName: 'DatabaseJitModal-checkboxes-fieldset',
          }}
        />
      </Box>
    </Box>
  );

  const renderedModalFooterActionBar = (
    <Box tag='section' className='DatabaseJitModal-footerActionBar'>
      <Button
        kind='outlined'
        size='medium'
        onClick={() => setIsDatabaseJitCreateAccount(false)}>
        {localize(msgs.pam.common.cancel)}
      </Button>
      <Button
        isDisabled={getCheckedNames().length === 0}
        kind='filled'
        size='medium'
        onClick={() => handleSave()}>
        {localize(msgs.pam.common.save)}
      </Button>
    </Box>
  );

  return (
    <ModalIsland
      className='DatabaseJitModal'
      classNames={{
        sectionClassName: 'DatabaseJitModal-section',
        headerClassName: 'DatabaseJitModal-header',
        bodyClassName: 'DatabaseJitModal-body',
      }}
      onClose={() => setIsDatabaseJitCreateAccount(false)}
      isOpen={isOpen}
      title={localize(msgs.pam.cts.db.jitTempPrivAcctModalTitle)}
      subtitle={
        <Typography kind='body1' color='--color-foreground-neutral-subtle'>
          {localize(msgs.pam.cts.db.jitTempAcctModalSubTitle)}
        </Typography>
      }
      contentBody={renderedModalContent}
      headerIcon={<Icon kind='AddCircle' size='medium' />}
      sectionRef={databaseJitModalContentRef}
      FooterActionBarComp={
        <FooterActionBar
          size='large'
          className='DatabaseJitModal-footerActionBarWrapper'>
          {renderedModalFooterActionBar}
        </FooterActionBar>
      }
    />
  );
}

DatabaseJitModalIsland.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsDatabaseJitCreateAccount: PropTypes.func.isRequired,
  setDatabaseJitAccountInfo: PropTypes.func.isRequired,
  databaseJitAccountInfo: PropTypes.shape({
    entitlements: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  setAccountSelection: PropTypes.func.isRequired,
  databaseJitModalContentRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]).isRequired,
};

export default DatabaseJitModalIsland;
